/* ============================================================
   RelationEditor — relation 속성 셀 편집기
   - 칩(기존 RelationCell 과 동일 스타일)은 Link 로 /page/<id> 이동 유지
   - ＋(또는 빈 셀) 클릭 → 대상 DB 행 검색 팝오버 → 다중 선택/해제
   - 저장 포맷(canonical): row page id 문자열 배열 ['<uuid>', ...]
     (노션 임포트 산출 [{id,title}] 객체 배열도 읽기 하위호환 — relationTitles 참조)
   - 대상 DB 데이터는 useRelationContext.ensureTarget 으로 DB 단위 1회 로드
   ============================================================ */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { relationIds, UUID_RE } from './computeValue';

function useOutsideClose(ref, onClose) {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
}

const CHIP_STYLE = { textDecoration: 'none', cursor: 'pointer', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

export default function RelationEditor({ col, value, onChange, dbx }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useOutsideClose(ref, () => setOpen(false));

  const ids = useMemo(() => relationIds(value), [value]);
  const dbId = col.relationDb && UUID_RE.test(String(col.relationDb)) ? col.relationDb : null;
  const target = dbId ? dbx.targets[dbId] : null;

  // 팝오버 열릴 때 대상 DB 행을 (아직 없으면) 1회 batch 로드
  useEffect(() => {
    if (open && dbId) dbx.ensureTarget(dbId);
  }, [open, dbId]); // eslint-disable-line

  const titleOf = (id) => {
    // 값이 [{id,title}] 객체 배열(임포트 포맷)이면 그 title 우선
    if (Array.isArray(value)) {
      const it = value.find((x) => x && typeof x === 'object' && (x.id === id || x.page_id === id));
      if (it && (it.title || it.name)) return it.title || it.name;
    }
    const t = dbx.titleById.get(id);
    if (t) return t.title || '제목 없음';
    const tr = target?.rowsById?.[id];
    if (tr) return tr.title || '제목 없음';
    return '(접근 불가)';
  };

  const toggle = (id) => {
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    onChange(col.key, next); // 저장: row page id 배열
  };

  const list = useMemo(() => {
    const rows = target?.rows || [];
    const s = q.trim().toLowerCase();
    return rows.filter((r) => !s || (r.title || '').toLowerCase().includes(s));
  }, [target, q]);

  // 레거시 평문 값 ("3개 연결" 등) — 배열이 아니면 그대로 노출하되 편집 가능
  const legacyText = !Array.isArray(value) && value != null && value !== '' ? String(value) : '';

  return (
    <div className="cell rel-cell" ref={ref}>
      {ids.map((id) => (
        <Link
          className="tag tg rel-chip"
          key={id}
          to={`/page/${id}`}
          title={titleOf(id)}
          style={CHIP_STYLE}
          onClick={(e) => e.stopPropagation()}
        >
          {titleOf(id)}
        </Link>
      ))}
      {legacyText && ids.length === 0 && (
        <span
          className="tag-trigger muted"
          title="가져온 평문 값 — 편집하면 관계 링크로 바뀝니다"
          onClick={() => setOpen((o) => !o)}
        >
          {legacyText}
        </span>
      )}
      <button
        className={`rel-edit${ids.length || legacyText ? '' : ' empty'}`}
        onClick={() => setOpen((o) => !o)}
        title="관계 편집"
      >
        {ids.length || legacyText ? '＋' : '비어 있음'}
      </button>
      {open && (
        <div className="opt-pop rel-pop">
          {!dbId && (
            <div className="rel-hint">
              속성 메뉴(컬럼 헤더 클릭)에서 대상 데이터베이스를 먼저 선택하세요.
            </div>
          )}
          {dbId && (
            <>
              <div className="ps-search">
                <input
                  autoFocus
                  value={q}
                  placeholder="페이지 제목 검색…"
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
                />
              </div>
              {(!target || target.loading) && <div className="rel-hint">불러오는 중…</div>}
              {target?.error && <div className="rel-hint">대상 데이터베이스에 접근할 수 없습니다.</div>}
              {target && !target.loading && !target.error && (
                <div className="opt-list">
                  {list.length === 0 && <div className="rel-hint">결과가 없습니다.</div>}
                  {list.map((r) => {
                    const on = ids.includes(r.page_id);
                    return (
                      <div
                        key={r.page_id}
                        className={`opt-item ps-item${on ? ' on' : ''}`}
                        onClick={() => toggle(r.page_id)}
                      >
                        <span>{r.icon || '📄'}</span>
                        <span className="ps-name">{r.title || '제목 없음'}</span>
                        {on && <span className="ps-check">✓</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
          {ids.length > 0 && (
            <div className="rel-selected">
              {ids.map((id) => (
                <span className="tag tg rel-chip" key={id} title={titleOf(id)}>
                  {titleOf(id)}
                  <button className="rel-x" onClick={() => toggle(id)} title="연결 해제">×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
